import { z } from "zod";

import {
  patientBlockSchema,
  providerBlockSchema,
  locationBlockSchema,
  coverageBlockSchema,
  referralInformationBlockSchema,
  documentInformationBlockSchema,
  adminBlockSchema,
  continuityOfCareBlockSchema,
  priorAuthorizationBlockSchema,
  progressNoteBlockSchema,
  imagingResultsBlockSchema,
  labResultsBlockSchema,
  medicalRecordRequestBlockSchema,
  referralResponseBlockSchema,
  dmeBlockSchema,
  notificationBlockSchema,
  emergencySummaryBlockSchema,
  carePlanBlockSchema,
  medicalHistoryBlockSchema,
  consentBlockSchema,
  idCardBlockSchema,
  insuranceBlockSchema,
  intakeBlockSchema,
  legalBlockSchema,
  operationNoteBlockSchema,
  clearanceRequestBlockSchema,
  financialBlockSchema,
  procedureBlockSchema,
  pharmacyRelatedBlockSchema,
} from "@/src/models/blocks/block";
import { BlockKey } from "@/src/models/blocks/block-types";
import { Classification } from "@/src/models/constants/classifications";

// Referral
export const referralEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.PRIMARY_COVERAGE]: z.lazy(() => coverageBlockSchema.nullable()),
  [BlockKey.SECONDARY_COVERAGE]: z.lazy(() => coverageBlockSchema.nullable()),
  [BlockKey.PRIMARY_CARE_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.REFERRAL_INFORMATION]: z.lazy(() => referralInformationBlockSchema.nullable()),
  [BlockKey.PRIOR_AUTHORIZATION]: z.lazy(() => priorAuthorizationBlockSchema.nullable()),
});
export type ReferralEnrichedContent = z.infer<typeof referralEnrichedContentSchema>;

export const referralBlockKeys = Object.keys(
  referralEnrichedContentSchema.shape
) as (keyof typeof referralEnrichedContentSchema.shape)[];
export type ReferralBlockKey = (typeof referralBlockKeys)[number];

// Identification Card
export const identificationCardEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ID_CARD]: z.lazy(() => idCardBlockSchema.nullable()),
});
export type IdentificationCardEnrichedContent = z.infer<typeof identificationCardEnrichedContentSchema>;

export const identificationCardBlockKeys = Object.keys(
  identificationCardEnrichedContentSchema.shape
) as (keyof typeof identificationCardEnrichedContentSchema.shape)[];
export type IdentificationCardBlockKey = (typeof identificationCardBlockKeys)[number];

// Insurance Card
export const insuranceCardEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.PRIMARY_COVERAGE]: z.lazy(() => coverageBlockSchema.nullable()),
  [BlockKey.SECONDARY_COVERAGE]: z.lazy(() => coverageBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.INSURANCE]: z.lazy(() => insuranceBlockSchema.nullable()),
});
export type InsuranceCardEnrichedContent = z.infer<typeof insuranceCardEnrichedContentSchema>;

export const insuranceCardBlockKeys = Object.keys(
  insuranceCardEnrichedContentSchema.shape
) as (keyof typeof insuranceCardEnrichedContentSchema.shape)[];
export type InsuranceCardBlockKey = (typeof insuranceCardBlockKeys)[number];

// Prior Authorization
export const priorAuthorizationEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.PRIOR_AUTHORIZATION]: z.lazy(() => priorAuthorizationBlockSchema.nullable()),
  [BlockKey.DOCUMENT_INFORMATION]: z.lazy(() => documentInformationBlockSchema.nullable()),
});
export type PriorAuthorizationEnrichedContent = z.infer<typeof priorAuthorizationEnrichedContentSchema>;

export const priorAuthorizationBlockKeys = Object.keys(
  priorAuthorizationEnrichedContentSchema.shape
) as (keyof typeof priorAuthorizationEnrichedContentSchema.shape)[];
export type PriorAuthorizationBlockKey = (typeof priorAuthorizationBlockKeys)[number];

// Progress Note
export const progressNoteEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.PROGRESS_NOTE]: z.lazy(() => progressNoteBlockSchema.nullable()),
  [BlockKey.DOCUMENT_INFORMATION]: z.lazy(() => documentInformationBlockSchema.nullable()),
});
export type ProgressNoteEnrichedContent = z.infer<typeof progressNoteEnrichedContentSchema>;

export const progressNoteBlockKeys = Object.keys(
  progressNoteEnrichedContentSchema.shape
) as (keyof typeof progressNoteEnrichedContentSchema.shape)[];
export type ProgressNoteBlockKey = (typeof progressNoteBlockKeys)[number];

// Imaging Results
export const imagingResultsEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.IMAGING_RESULTS]: z.lazy(() => imagingResultsBlockSchema.nullable()),
});
export type ImagingResultsEnrichedContent = z.infer<typeof imagingResultsEnrichedContentSchema>;

export const imagingResultsBlockKeys = Object.keys(
  imagingResultsEnrichedContentSchema.shape
) as (keyof typeof imagingResultsEnrichedContentSchema.shape)[];
export type ImagingResultsBlockKey = (typeof imagingResultsBlockKeys)[number];

// Lab Results
export const labResultsEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.LAB_RESULTS]: z.lazy(() => labResultsBlockSchema.nullable()),
  [BlockKey.DOCUMENT_INFORMATION]: z.lazy(() => documentInformationBlockSchema.nullable()),
});
export type LabResultsEnrichedContent = z.infer<typeof labResultsEnrichedContentSchema>;

export const labResultsBlockKeys = Object.keys(
  labResultsEnrichedContentSchema.shape
) as (keyof typeof labResultsEnrichedContentSchema.shape)[];
export type LabResultsBlockKey = (typeof labResultsBlockKeys)[number];

// Consent — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated CONSENT block.
export const consentEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.CONSENT]: z.lazy(() => consentBlockSchema.nullable()),
});
export type ConsentEnrichedContent = z.infer<typeof consentEnrichedContentSchema>;

// Operation Note — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated OPERATION_NOTE block.
export const operationNoteEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.OPERATION_NOTE]: z.lazy(() => operationNoteBlockSchema.nullable()),
});
export type OperationNoteEnrichedContent = z.infer<typeof operationNoteEnrichedContentSchema>;

// Intake — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated INTAKE block.
export const intakeEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.INTAKE]: z.lazy(() => intakeBlockSchema.nullable()),
});
export type IntakeEnrichedContent = z.infer<typeof intakeEnrichedContentSchema>;

// Insurance — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated INSURANCE block.
export const insuranceEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.INSURANCE]: z.lazy(() => insuranceBlockSchema.nullable()),
});
export type InsuranceEnrichedContent = z.infer<typeof insuranceEnrichedContentSchema>;

// Legal — mirrors the provider-field entity-block layout but swaps DOCUMENT_INFORMATION for the curated LEGAL block.
export const legalEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.LEGAL]: z.lazy(() => legalBlockSchema.nullable()),
});
export type LegalEnrichedContent = z.infer<typeof legalEnrichedContentSchema>;

// Clearance Request
export const clearanceRequestEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.CLEARANCE_REQUEST]: z.lazy(() => clearanceRequestBlockSchema.nullable()),
});
export type ClearanceRequestEnrichedContent = z.infer<typeof clearanceRequestEnrichedContentSchema>;

// Financial
export const financialEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.FINANCIAL]: z.lazy(() => financialBlockSchema.nullable()),
});
export type FinancialEnrichedContent = z.infer<typeof financialEnrichedContentSchema>;

// Procedure
export const procedureEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROCEDURE]: z.lazy(() => procedureBlockSchema.nullable()),
});
export type ProcedureEnrichedContent = z.infer<typeof procedureEnrichedContentSchema>;

// Generic
export const genericEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.DOCUMENT_INFORMATION]: z.lazy(() => documentInformationBlockSchema.nullable()),
});

const genericShape = genericEnrichedContentSchema.shape;

export const adminEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.ADMIN]: z.lazy(() => adminBlockSchema.nullable()),
});
export const providerFieldEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
});
// Status requests additionally extract the referring provider (the office requesting the update).
export const referralStatusRequestEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.REFERRING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
});
export const medicalRecordRequestEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.MEDICAL_RECORD_REQUEST]: z.lazy(() => medicalRecordRequestBlockSchema.nullable()),
});
export const referralResponseEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.REFERRAL_RESPONSE]: z.lazy(() => referralResponseBlockSchema.nullable()),
});
export const dmeEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.DME]: z.lazy(() => dmeBlockSchema.nullable()),
});
export const notificationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.NOTIFICATION]: z.lazy(() => notificationBlockSchema.nullable()),
});
export const emergencySummaryEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.EMERGENCY_SUMMARY]: z.lazy(() => emergencySummaryBlockSchema.nullable()),
});
export const carePlanEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.CARE_PLAN]: z.lazy(() => carePlanBlockSchema.nullable()),
});
export const referrerInformationEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
});
export const medicalHistoryEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.MEDICAL_HISTORY]: z.lazy(() => medicalHistoryBlockSchema.nullable()),
});
export const progressNoteWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.PROGRESS_NOTE]: z.lazy(() => progressNoteBlockSchema.nullable()),
});
export const imagingResultsWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.IMAGING_RESULTS]: z.lazy(() => imagingResultsBlockSchema.nullable()),
});
export const continuityOfCareWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.CONTINUITY_OF_CARE]: z.lazy(() => continuityOfCareBlockSchema.nullable()),
});
export const labResultsWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.LAB_RESULTS]: z.lazy(() => labResultsBlockSchema.nullable()),
});

export const referralRequestEnrichedContentSchema = z.object({
  ...genericEnrichedContentSchema.shape,
  [BlockKey.REFERRING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.PRIMARY_COVERAGE]: z.lazy(() => coverageBlockSchema.nullable()),
  [BlockKey.REFERRAL_INFORMATION]: z.lazy(() => referralInformationBlockSchema.nullable()),
});

// Pharmacy Related
export const pharmacyRelatedEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: z.lazy(() => patientBlockSchema.nullable()),
  [BlockKey.ORDERING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.RECEIVING_PROVIDER]: z.lazy(() => providerBlockSchema.nullable()),
  [BlockKey.REFERRING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.RECEIVING_LOCATION]: z.lazy(() => locationBlockSchema.nullable()),
  [BlockKey.DOCUMENT_INFORMATION]: z.lazy(() => documentInformationBlockSchema.nullable()),
});
export type GenericEnrichedContent = z.infer<typeof genericEnrichedContentSchema>;

export const genericBlockKeys = Object.keys(
  genericEnrichedContentSchema.shape
) as (keyof typeof genericEnrichedContentSchema.shape)[];
export type GenericBlockKey = (typeof genericBlockKeys)[number];
export type PharmacyRelatedEnrichedContent = z.infer<typeof pharmacyRelatedEnrichedContentSchema>;

export const pharmacyRelatedWithDocumentInformationEnrichedContentSchema = z.object({
  ...pharmacyRelatedEnrichedContentSchema.shape,
  [BlockKey.PHARMACY_RELATED]: z.lazy(() => pharmacyRelatedBlockSchema.nullable()),
});

// Enriched Content Union
export const enrichedContentUnionSchema = z
  .union([
    adminEnrichedContentSchema,
    referralEnrichedContentSchema,
    identificationCardEnrichedContentSchema,
    insuranceCardEnrichedContentSchema,
    priorAuthorizationEnrichedContentSchema,
    progressNoteEnrichedContentSchema,
    progressNoteWithDocumentInformationEnrichedContentSchema,
    imagingResultsWithDocumentInformationEnrichedContentSchema,
    continuityOfCareWithDocumentInformationEnrichedContentSchema,
    labResultsWithDocumentInformationEnrichedContentSchema,
    imagingResultsEnrichedContentSchema,
    labResultsEnrichedContentSchema,
    pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
    pharmacyRelatedEnrichedContentSchema,
    providerFieldEnrichedContentSchema,
    medicalRecordRequestEnrichedContentSchema,
    referralResponseEnrichedContentSchema,
    referralRequestEnrichedContentSchema,
    dmeEnrichedContentSchema,
    notificationEnrichedContentSchema,
    emergencySummaryEnrichedContentSchema,
    carePlanEnrichedContentSchema,
    medicalHistoryEnrichedContentSchema,
    consentEnrichedContentSchema,
    operationNoteEnrichedContentSchema,
    intakeEnrichedContentSchema,
    insuranceEnrichedContentSchema,
    legalEnrichedContentSchema,
    clearanceRequestEnrichedContentSchema,
    financialEnrichedContentSchema,
    procedureEnrichedContentSchema,
    genericEnrichedContentSchema,
  ])
  .nullable();

export type EnrichedContentIndexable = Partial<Record<BlockKey, unknown>>;
type EnrichedContentUnion = z.infer<typeof enrichedContentUnionSchema>;
type WithIndexable<T> = T extends null ? null : T & EnrichedContentIndexable;
export type EnrichedContent = WithIndexable<EnrichedContentUnion>;

// TODO: Use this new mapping after fixing upstream (extraction) + downstream (document, reviewedContent readers)
// key disparities for related classifications (e.g. imaging results vs imaging results ct)

// type EnrichedContentSchema =
//   | typeof referralEnrichedContentSchema
//   | typeof identificationCardEnrichedContentSchema
//   | typeof insuranceCardEnrichedContentSchema
//   | typeof priorAuthorizationEnrichedContentSchema
//   | typeof progressNoteEnrichedContentSchema
//   | typeof imagingResultsEnrichedContentSchema
//   | typeof labResultsEnrichedContentSchema;
//
// export const ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION: Partial<Record<Classification, EnrichedContentSchema>> = {
//   ...Object.fromEntries(REFERRAL_CLASSIFICATIONS.map((c) => [c, referralEnrichedContentSchema])),
//   ...Object.fromEntries(IDENTIFICATION_CLASSIFICATIONS.map((c) => [c, identificationCardEnrichedContentSchema])),
//   [Classification.INSURANCE_CARD]: insuranceCardEnrichedContentSchema,
//   [Classification.PRIOR_AUTHORIZATION]: priorAuthorizationEnrichedContentSchema,
//   ...Object.fromEntries(PROGRESS_NOTE_CLASSIFICATIONS.map((c) => [c, progressNoteEnrichedContentSchema])),
//   ...Object.fromEntries(IMAGING_RESULTS_CLASSIFICATIONS.map((c) => [c, imagingResultsEnrichedContentSchema])),
//   ...Object.fromEntries(LAB_RESULTS_CLASSIFICATIONS.map((c) => [c, labResultsEnrichedContentSchema])),
// };

export const ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION = {
  [Classification.ADMIN]: adminEnrichedContentSchema,
  [Classification.ADMIN_DISABILITY_FORMS]: adminEnrichedContentSchema,
  [Classification.OTHER]: adminEnrichedContentSchema,
  [Classification.REFERRAL]: referralEnrichedContentSchema,
  [Classification.REFERER_INFORMATION]: referrerInformationEnrichedContentSchema,
  [Classification.REFERRAL_RESPONSE]: referralResponseEnrichedContentSchema,
  [Classification.REFERRAL_REQUEST]: referralRequestEnrichedContentSchema,
  [Classification.CONSENT_FOR_RECORDS_SPECIALIST]: referralRequestEnrichedContentSchema,
  [Classification.RESPONSE_TO_VH_REQUEST]: referralEnrichedContentSchema,
  [Classification.IDENTIFICATION_CARD]: identificationCardEnrichedContentSchema,
  [Classification.ADMIN_LEAD_FORM]: providerFieldEnrichedContentSchema,
  [Classification.INSURANCE_CARD]: insuranceCardEnrichedContentSchema,
  [Classification.INSURANCE_COMMUNICATION]: insuranceEnrichedContentSchema,
  [Classification.INSURANCE_LETTER_OF_ASSURANCE]: insuranceEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE]: progressNoteEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_PT]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_ER_REPORT]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_VITAL_CHART]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: providerFieldEnrichedContentSchema,
  [Classification.CONTINUITY_OF_CARE]: continuityOfCareWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS]: imagingResultsEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CT]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ENDOSCOPY]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CARDIAC]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_STRESS_TEST]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ULTRASOUND]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_X_RAY]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_DEXA]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ECHO]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EEG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EKG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EMG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ESOPHAGRAM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_HOLTER_MONITOR]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_MAMMOGRAM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_MRI]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_PULMONARY_FUNCTION]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_RETINAL]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_NM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_PET]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.LAB_RESULTS]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_BP]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.LAB_RESULTS_ORDER_FORM]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED]: pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CARE_CONSIDERATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CLARIFICATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CONTROLED_SUBSTANCES_POLICY]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_DRUG_MONITORING_PROGRAM]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_MED_RECONCILIATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_MEDICATION_LIST]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_NON_ADHERENCE]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_REFILL]: pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_REFILL_MANY]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_ALERT]: pharmacyRelatedEnrichedContentSchema,
  [Classification.ORDER_LAB]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_IMAGING]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_SIGNED]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_CLARIFICATION]: providerFieldEnrichedContentSchema,
  [Classification.DME_ORDER]: dmeEnrichedContentSchema,
  [Classification.FINANCIAL]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_BILLING_RECORDS]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS_CREDIT_CARD]: financialEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_RESPONSE]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_PRE_OP]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_SURGICAL]: clearanceRequestEnrichedContentSchema,
  [Classification.PROCEDURE]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_CARDIAC]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_CARDIAC_VEIN]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_REHAB]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_DEVICE_REQUEST]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_DEVICE_INFO]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_GI]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_INVENTORY_LIST]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_SLEEP]: procedureEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_LEGAL]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_OTHER]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_SSA]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_RESPONSE]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_HOME_HEALTH]: providerFieldEnrichedContentSchema,
  [Classification.REFERRAL_STATUS_REQUEST]: referralStatusRequestEnrichedContentSchema,
  [Classification.DME_REQUEST]: dmeEnrichedContentSchema,
  [Classification.DME_RESPONSE]: dmeEnrichedContentSchema,
  [Classification.NOTIFICATION]: notificationEnrichedContentSchema,
  [Classification.NOTIFICATION_HOSPITAL_ADMIT]: notificationEnrichedContentSchema,
  [Classification.NOTIFICATION_HOSPITAL_DISCHARGE]: notificationEnrichedContentSchema,
  [Classification.EMERGENCY_SUMMARY]: emergencySummaryEnrichedContentSchema,
  [Classification.CARE_PLAN]: carePlanEnrichedContentSchema,
  [Classification.CARE_PLAN_REMINDER]: carePlanEnrichedContentSchema,
  [Classification.CONSENT]: consentEnrichedContentSchema,
  [Classification.OPERATION_NOTE]: operationNoteEnrichedContentSchema,
  [Classification.INTAKE]: intakeEnrichedContentSchema,
  [Classification.INTAKE_DIALYSIS_QUESTIONAIRE]: intakeEnrichedContentSchema,
  [Classification.LEGAL]: legalEnrichedContentSchema,
  [Classification.LEGAL_ADVANCED_CARE_DIRECTIVE]: legalEnrichedContentSchema,
  [Classification.LEGAL_COURT_DOCUMENTS]: legalEnrichedContentSchema,
  [Classification.LEGAL_POWER_OF_ATTORNEY]: legalEnrichedContentSchema,
  [Classification.LAB_RESULTS_OTHER]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_PATHOLOGY]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_BILIRUBIN]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_HEARING_TEST]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_PKU]: labResultsEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_RESPONSE]: medicalHistoryEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY]: medicalHistoryEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_IMMUNIZATION]: medicalHistoryEnrichedContentSchema,
  [Classification.ORDER_FROM_PHYSICIAN]: adminEnrichedContentSchema,
} as const;

export function getEnrichedContentSchemaForClassification(classification: Classification) {
  return (
    ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION[
      classification as keyof typeof ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION
    ] ?? genericEnrichedContentSchema
  );
}

export function getBlockKeysForClassification(classification: Classification): readonly BlockKey[] {
  const schema = getEnrichedContentSchemaForClassification(classification);

  return Object.keys(schema.shape) as BlockKey[];
}
